// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents, CustomizerConfirmRemoveEvent } from '@theme/events';
import {
  openKeyboardSheet,
  closeKeyboardSheet,
  syncKeyboardSheet,
} from '@theme/custom.customizer-keyboard';
import { normalizePhotoFile } from '@theme/custom.customizer-photo-ingest';
import { fitsCartAdd } from '@theme/custom.customizer-commit';
import { capText } from '@theme/custom.customizer-store';
import { findStyleControl, readIsland } from '@theme/custom.customizer-resolve';
import { isFaceModeControl } from '@theme/custom.customizer-model';
import { track } from '@theme/custom.customizer-analytics';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */
/** @typedef {import('@theme/custom.customizer-preview').CustomizerPreview} CustomizerPreview */
/** @typedef {{ key: string, in: string[] }} VisibleCondition */
/** @typedef {{ family: string, url?: string }} FontAsset */

/**
 * Characters a text control strips, keyed by its `charset`; absent means no restriction. Mirrors
 * assets/custom.name-jewelry.js and the pattern in blocks/custom-name-jewelry.liquid. A Map, so a
 * stray stored value such as `constructor` misses instead of finding an Object.prototype member.
 * @type {Map<string, RegExp>}
 */
const CHARSET_DISALLOW = new Map([
  ['letters', /[^A-Za-z ]+/g],
  ['alphanumeric', /[^A-Za-z0-9 ]+/g],
  ['numeric', /[^0-9]+/g],
  ['coordinates', /[^A-Za-z0-9 .°]+/g],
]);

/**
 * A single personalization control inside a customizer step (message / monogram / choice / font
 * thumbnails / upload / comments / clip-art).
 *
 * The markup is server-rendered by snippets/custom.customizer-control.liquid; this component
 * captures input and pushes it to the store via the host `<custom-customizer>`
 * (`store.updateControl(key, value)`).
 * It also honours `visibleWhen` (an AND of `{ key, in }` conditions), re-evaluating on each
 * `customizer:update`, and drives the shared Remove-confirm dialog.
 *
 * Wiring (leading-slash `on:` targets this component):
 *   message line inputs → `on:input='/inputMessage'`, per-line clear → `on:click='/clearLine'`,
 *                         `on:focus='/focusField'` / `on:blur='/blurField'` (editing state + the
 *                         mobile keyboard sheet), `on:keydown='/lineKeydown'` (Return/Backspace)
 *   monogram / comments → `on:input='/input'` (+ monogram: focus/blur as above,
 *                         `on:keydown='/fieldKeydown'` - Return acts as Done)
 *   Preview / Done      → `on:click='/previewField'` / `on:click='/doneField'`
 *   choice / font row   → `on:click='/pick'` (row carries `data-value`)
 *   file input          → `on:change='/upload'`
 *   Remove              → `on:click='/requestRemove'`
 *
 * @extends Component
 */
class CustomizerControl extends Component {
  /** Monotonic ticket for in-flight ingest conversions: only the newest pick may land. */
  #ingestSeq = 0;

  /** True while a conversion is running, so store broadcasts don't stomp the processing state. */
  #ingestBusy = false;

  /** The store that owns the pending upload. @type {CustomizerStore | undefined} */
  #ingestStore;

  /** Why the last upload failed, so a banner raised again says the same thing. */
  #uploadErrorKind = /** @type {'format' | 'size' | 'too-large' | 'decode'} */ ('decode');

  /** @type {{ store: CustomizerStore | undefined, value: unknown } | null} */
  #fieldState = null;

  /** Deferred exit-editing timer after blur, so a Preview/Done/clear click lands first. */
  #editTimer = 0;

  /** The step body's scroll position when the keyboard sheet opened - the browser may scroll the
   *  (invisible) body to "reveal" the focused input, so it's put back when the sheet closes. */
  #sheetScrollTop = 0;

  /** The keyboard edit sheet is a mobile pattern; desktop keeps the inline fields (no keyboard). */
  static #mobileSheet = window.matchMedia('(max-width: 1023px)');

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.customizerUpdate, this.#syncVisibility);
    document.addEventListener(ThemeEvents.customizerRemoveConfirmed, this.#handleRemoveConfirmed);
    this.#applyFonts();
    this.#syncVisibility();
    this.#refreshMessageUi();
    this.#refreshRemove();

    // Rows must exist before reflection, and reflection must clear stale text before capping it.
    if (this.dataset.controlType === 'text') {
      document.addEventListener(ThemeEvents.customizerUpdate, this.#syncLineRows);
      this.#syncLineRows();
    }

    // The selected style lives on a sibling control, so the label follows every store change.
    if (this.dataset.controlType === 'text' || this.dataset.controlType === 'monogram') {
      document.addEventListener(ThemeEvents.customizerUpdate, this.#syncStyleLabel);
      this.#syncStyleLabel();
    }

    // Registered AFTER the row sync above so a restored message has rows to land in; called
    // directly since a restored value may predate any broadcast.
    if (this.dataset.controlType === 'text' || this.dataset.controlType === 'monogram') {
      document.addEventListener(ThemeEvents.customizerUpdate, this.#restoreFieldValue);
      this.#restoreFieldValue();
    }

    if (this.dataset.controlType === 'text') {
      document.addEventListener(ThemeEvents.customizerUpdate, this.#applyLineCap);
      this.#applyLineCap();
    }

    // macOS Safari and Firefox don't focus a <button> on press, so the blur exit timer can hide an
    // editing tool mid-press; holding focus through a mouse press keeps its click alive.
    if (this.dataset.controlType === 'monogram' || this.dataset.controlType === 'text') {
      this.addEventListener('pointerdown', this.#keepFieldFocus);
    }

    // A choice can also be written store-side (the engrave router preselects a method), so follow
    // the store or the marked card lags behind it.
    if (this.dataset.controlType === 'choice') {
      document.addEventListener(ThemeEvents.customizerUpdate, this.#reflectChoice);
      this.#reflectChoice();
    }

    // The textarea is the only writer, but a section re-render can replace the DOM while the store
    // keeps the value, so mirror it back on every change.
    if (this.dataset.controlType === 'comments') {
      document.addEventListener(ThemeEvents.customizerUpdate, this.#reflectComments);
      this.#reflectComments();
    }

    // Mirror the store value into the dropzone on every change so Remove reverts it too. Browse
    // needs no handler of its own: it works natively through the label.
    if (this.dataset.controlType === 'upload') {
      document.addEventListener(ThemeEvents.customizerUpdate, this.#reflectUpload);
      this.#reflectUpload();
      const zone = this.#uploadZone;
      if (zone) {
        zone.addEventListener('dragover', this.#onDragOver);
        zone.addEventListener('dragleave', this.#onDragLeave);
        zone.addEventListener('drop', this.#onDrop);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#ingestBusy) {
      this.#ingestSeq++;
      this.#ingestBusy = false;
      this.#ingestStore?.setUploadState(this.#key, 'error');
    }
    clearTimeout(this.#editTimer);
    closeKeyboardSheet(this);
    this.removeEventListener('pointerdown', this.#keepFieldFocus);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#syncVisibility);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#syncLineRows);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#applyLineCap);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#syncStyleLabel);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#restoreFieldValue);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#reflectChoice);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#reflectComments);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#reflectUpload);
    document.removeEventListener(
      ThemeEvents.customizerRemoveConfirmed,
      this.#handleRemoveConfirmed
    );
    const zone = this.#uploadZone;
    if (zone) {
      zone.removeEventListener('dragover', this.#onDragOver);
      zone.removeEventListener('dragleave', this.#onDragLeave);
      zone.removeEventListener('drop', this.#onDrop);
    }
  }

  /** @returns {CustomizerStore | undefined} */
  get #store() {
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    return host?.store;
  }

  /** @returns {string} */
  get #key() {
    return this.dataset.controlKey ?? '';
  }

  /** @returns {string} The owning customizer instance's config id (for scoping document events). */
  get #configId() {
    const host = /** @type {HTMLElement | null} */ (this.closest('custom-customizer'));
    return host?.dataset.configId ?? '';
  }

  /**
   * A message line changed: recompute the joined value, push it, and refresh counters / clears.
   * While editing, a line typed up to its cap rolls the caret onto the next row ("text wraps as the
   * user reaches max character count") - only on real insertions with the caret at the end, so
   * mid-line edits and deletions never yank focus.
   * @param {Event} event
   */
  inputMessage(event) {
    const input = event.target;
    if (input instanceof HTMLInputElement) this.#enforceCharset(input);
    this.#store?.updateControl(this.#key, this.#collectMessage());
    this.#refreshMessageUi();
    this.#refreshRemove();
    this.#syncSheetRows();

    const inputType = event instanceof InputEvent ? (event.inputType ?? '') : '';
    if (
      input instanceof HTMLInputElement &&
      this.hasAttribute('data-editing') &&
      inputType.startsWith('insert') &&
      input.maxLength > 0 &&
      input.value.length >= input.maxLength &&
      input.selectionEnd === input.value.length
    ) {
      this.#focusLine(Number(input.dataset.lineIndex) + 1);
    }
  }

  /**
   * Drop characters this control's `charset` disallows, keeping the shopper's caret in place.
   * No charset admits a character an IME composes, so filtering mid-composition cannot destroy
   * legitimate input; absent charset is a no-op.
   * @param {HTMLInputElement} input
   */
  #enforceCharset(input) {
    const disallow = CHARSET_DISALLOW.get(this.dataset.charset ?? '');
    if (!disallow) return;
    const typed = input.value;
    const cleaned = typed.replace(disallow, '');
    if (cleaned === typed) return;
    const caret = input.selectionStart ?? typed.length;
    const kept = typed.slice(0, caret).replace(disallow, '').length;
    input.value = cleaned;
    input.setSelectionRange(kept, kept);
  }

  /**
   * Clear a single message line (the per-line ⊗), then re-derive the value.
   * @param {Event} event
   */
  clearLine(event) {
    const line = /** @type {Element} */ (event.target).closest('.custom-customizer-control__line');
    const input = line?.querySelector('.custom-customizer-control__line-input');
    if (input instanceof HTMLInputElement) {
      input.value = '';
      input.focus({ preventScroll: true });
    }
    this.#store?.updateControl(this.#key, this.#collectMessage());
    this.#refreshMessageUi();
    this.#refreshRemove();
    this.#syncSheetRows();
  }

  /**
   * Message-line keys: Return commits a line break by rolling focus to the next row (the native
   * keyboard's Return is the only way to add a line), Backspace on an emptied row walks back to the
   * previous one, chat-style. Rows beyond `maxLines` simply don't exist, so both are bounded.
   * @param {KeyboardEvent} event
   */
  lineKeydown(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const index = Number(input.dataset.lineIndex);
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#focusLine(index + 1);
    } else if (event.key === 'Backspace' && input.value === '' && index > 0) {
      event.preventDefault();
      this.#focusLine(index - 1);
    }
  }

  /**
   * Focus message line `index` (a no-op past the schema's `maxLines`), caret at its end. The row is
   * un-hidden first - a sheet-hidden row is `display: none`, which cannot take focus.
   *
   * `offsetHeight` flushes the reveal into layout first; `preventScroll` stops the browser
   * revealing a row already on screen, which on iOS pans the sheet off a live caret.
   * @param {number} index
   */
  #focusLine(index) {
    const target = this.#lineInputs()[index];
    if (!target) return;
    const line = target.closest('.custom-customizer-control__line');
    line?.removeAttribute('data-sheet-hidden');
    void target.offsetHeight;
    target.focus({ preventScroll: true });
    target.setSelectionRange(target.value.length, target.value.length);
    this.#syncSheetRows();
  }

  /**
   * Hand focus to this control's text entry as if the shopper had tapped it: the first empty
   * message row (else the last row - continue-typing semantics on a filled message), or the
   * monogram initials input, caret at the end. Returns false for control types with no text entry
   * (choice tiles, uploads, the clipart picker) so a caller can keep looking. Callers gate this to
   * desktop - on mobile a programmatic focus pops the software keyboard + the pinned edit sheet
   * over a view the shopper hasn't seen yet (tap-to-edit is the decided mobile UX).
   * @returns {boolean} True when focus was placed.
   */
  focusEntry() {
    const lines = this.#lineInputs();
    if (lines.length > 0) {
      const empty = lines.findIndex((line) => line.value === '');
      this.#focusLine(empty === -1 ? lines.length - 1 : empty);
      return true;
    }
    const input = this.querySelector('.custom-customizer-control__input--monogram');
    if (input instanceof HTMLInputElement) {
      input.focus({ preventScroll: true });
      input.setSelectionRange(input.value.length, input.value.length);
      return true;
    }
    return false;
  }

  /** While the keyboard sheet is open, only rows up to the last filled or focused one show, so
   *  the sheet grows upward. `data-sheet-hidden` bites inside the mobile editing sheet only. */
  #syncSheetRows() {
    if (this.dataset.controlType !== 'text' || !this.hasAttribute('data-editing')) return;
    const inputs = this.#lineInputs();
    let last = 0;
    inputs.forEach((input, index) => {
      if (input.value !== '' || input === document.activeElement) last = Math.max(last, index);
    });
    inputs.forEach((input, index) => {
      input
        .closest('.custom-customizer-control__line')
        ?.toggleAttribute('data-sheet-hidden', index > last);
    });
    syncKeyboardSheet();
  }

  /** Show every message row again (editing left - the idle block and desktop render them all). */
  #clearSheetRows() {
    for (const line of this.querySelectorAll('[data-sheet-hidden]')) {
      line.removeAttribute('data-sheet-hidden');
    }
  }

  /** Restore into empty fields so a store broadcast does not overwrite live typing. */
  #restoreFieldValue = () => {
    const store = this.#store;
    const value = store?.getControlValue(this.#key);
    const type = this.dataset.controlType;
    const reset =
      this.#stepSkipped() ||
      (this.#fieldState !== null &&
        (this.#fieldState.store !== store ||
          (typeof this.#fieldState.value === 'string' && this.#fieldState.value !== '')));
    this.#fieldState = { store, value };

    // An explicit clear wins over focus; otherwise an empty store may precede the input event.
    if (typeof value !== 'string' || value === '') {
      const fields =
        type === 'text'
          ? this.#lineInputs()
          : [this.querySelector('.custom-customizer-control__input')];
      let cleared = false;
      for (const field of fields) {
        if (!(field instanceof HTMLInputElement)) continue;
        if (field.value === '' || (!reset && field === document.activeElement)) continue;
        field.value = '';
        cleared = true;
      }
      if (!cleared) return;
      if (type === 'text') this.#refreshMessageUi();
      this.#refreshRemove();
      return;
    }

    if (type === 'text') {
      const lines = value.split('\n');
      this.#lineInputs().forEach((input, index) => {
        const line = lines[index];
        if (input.value === '' && line) input.value = line;
      });
      this.#refreshMessageUi();
      this.#refreshRemove();
      return;
    }

    const input = this.querySelector('.custom-customizer-control__input');
    if (!(input instanceof HTMLInputElement) || input.value !== '') return;
    input.value = value;
    this.#refreshRemove();
  };

  /** @returns {string} The message lines joined by newlines, with trailing empty lines trimmed. */
  #collectMessage() {
    const values = this.#lineInputs().map((input) => input.value);
    while (values.length && (values[values.length - 1] ?? '').trim() === '') values.pop();
    return values.join('\n');
  }

  /** @returns {HTMLInputElement[]} */
  #lineInputs() {
    return /** @type {HTMLInputElement[]} */ ([
      ...this.querySelectorAll('.custom-customizer-control__line-input'),
    ]);
  }

  /** Grow or shrink message rows to the control's `maxLines`. Slim configs strip that cap, so the
   *  real count only exists client-side; rows are cloned to keep the delegated `on:` wiring. */
  #syncLineRows = () => {
    const message = this.querySelector('[data-message]');
    if (!message) return;
    const doc = this.#store?.configSchema;
    /** @type {number | null} */
    let max = null;
    for (const step of doc?.steps ?? []) {
      for (const control of step.controls ?? []) {
        if (control?.key === this.#key) {
          max = Number(control.maxLines) || null;
          break;
        }
      }
      if (max !== null) break;
    }
    if (!max || max < 1) return;
    let lines = this.#lineInputs();
    while (lines.length > max) {
      const input = lines[lines.length - 1];
      if (!input || input.value !== '') break;
      input.closest('.custom-customizer-control__line')?.remove();
      lines = this.#lineInputs();
    }
    const template = lines[lines.length - 1]?.closest('.custom-customizer-control__line');
    if (!(template instanceof HTMLElement)) return;
    while (lines.length < max) {
      const row = /** @type {HTMLElement} */ (template.cloneNode(true));
      const index = lines.length;
      const input = row.querySelector('.custom-customizer-control__line-input');
      if (!(input instanceof HTMLInputElement)) return;
      input.value = '';
      input.id = `${this.#key}-${index}`;
      input.dataset.lineIndex = String(index);
      // Assumes the "Line N" copy holds an ASCII digit to swap: a localized string in another
      // numeral system would leave the clone's ordinal unchanged.
      input.placeholder = input.placeholder.replace(/\d+/, String(index + 1));
      const aria = input.getAttribute('aria-label');
      if (aria) input.setAttribute('aria-label', aria.replace(/\d+/, String(index + 1)));
      row.querySelector('.custom-customizer-control__line-clear')?.setAttribute('hidden', '');
      const count = row.querySelector('.custom-customizer-control__line-count');
      if (count?.textContent) count.textContent = count.textContent.replace(/^\d+/, '0');
      if (count) {
        count.id = `${input.id}-count`;
        input.setAttribute('aria-describedby', count.id);
      }
      row.removeAttribute('data-sheet-hidden');
      message.append(row);
      lines = this.#lineInputs();
    }
  };

  /**
   * The active character cap for the message lines: the selected font-style option's
   * `charsPerLine`, else the static `data-max-chars`, else null (uncapped). `charsPerLine` is a
   * single number (caps every row the same) or a per-row array (e.g. `[15,20,20,20,15]`, as the
   * imported preset provides); values are coerced with `Number()` since the import pipeline may
   * deliver strings. The font-style control is a sibling `choice` bound to the same render layer,
   * so switching font re-reads the cap live.
   * @returns {number | number[] | null}
   */
  #lineCap() {
    const styleControl = this.#styleControl();
    if (styleControl) {
      const raw = this.#store?.getControlValue(styleControl.key);
      const selected = typeof raw === 'string' ? raw : (styleControl.default ?? null);
      const option = styleControl.options?.find(
        (candidate) => (candidate.value || candidate.label) === selected
      );
      const caps = option?.charsPerLine;
      if (Array.isArray(caps) && caps.length > 0) return caps;
      const single = Number(caps);
      if (Number.isFinite(single) && single > 0) return single;
    }
    return this.#staticCap();
  }

  /**
   * The sibling `choice` style control for this control's render layer (the font-style picker), or
   * null. Shared by the per-line cap (#lineCap) and the engraving label (#syncStyleLabel), so both
   * read the same source of truth for the selected style.
   * @returns {import('@theme/custom.customizer-store').CustomizerControl | null}
   */
  #styleControl() {
    return findStyleControl(
      this.#store?.configSchema,
      this.dataset.bindsLayer,
      this.dataset.controlKey
    );
  }

  /** Mirror the selected style name into the engraving label, falling back to the schema default
   * so the label reads correctly before the shopper picks. */
  #syncStyleLabel = () => {
    const span = this.querySelector('[data-style-label]');
    if (!(span instanceof HTMLElement)) return;
    const styleControl = this.#styleControl();
    let label = '';
    if (styleControl) {
      const raw = this.#store?.getControlValue(styleControl.key);
      const selected = typeof raw === 'string' && raw ? raw : (styleControl.default ?? null);
      const option = styleControl.options?.find(
        (candidate) => (candidate.value || candidate.label) === selected
      );
      label = option?.label ?? '';
    }
    span.textContent = label;
  };

  /**
   * The static `data-max-chars` fallback cap, or null (uncapped).
   * @returns {number | null}
   */
  #staticCap() {
    const cap = Number(this.dataset.maxChars);
    return Number.isFinite(cap) && cap > 0 ? cap : null;
  }

  /** Apply the active per-line cap to the inputs, truncating a line a narrower
   * font just outgrew. */
  #applyLineCap = () => {
    const caps = this.#lineCap();
    let truncated = false;
    this.#lineInputs().forEach((input, index) => {
      // A font whose array is shorter than the row count caps the extra rows like its last entry.
      let cap = Number(Array.isArray(caps) ? (caps[index] ?? caps[caps.length - 1]) : caps);
      // An import-fed array can carry strings or holes; a row whose entry doesn't coerce to a
      // positive number falls back to the static cap instead of going uncapped.
      if (!(Number.isFinite(cap) && cap > 0) && Array.isArray(caps)) cap = this.#staticCap() ?? NaN;
      if (Number.isFinite(cap) && cap > 0) {
        input.maxLength = cap;
        if (input.value.length > cap) {
          input.value = capText(input.value, cap);
          truncated = true;
        }
      } else {
        input.removeAttribute('maxlength');
      }
    });
    if (truncated) this.#store?.updateControl(this.#key, this.#collectMessage());
    this.#refreshMessageUi();
  };

  /** Refresh each line's `n/max` counter and clear button. An uncapped row hides its counter
   * rather than printing a placeholder max: the number shown is always one the data produced. */
  #refreshMessageUi() {
    for (const input of this.#lineInputs()) {
      const line = input.closest('.custom-customizer-control__line');
      if (!line) continue;
      const count = line.querySelector('.custom-customizer-control__line-count');
      const clear = line.querySelector('.custom-customizer-control__line-clear');
      const capped = input.maxLength > 0;
      if (count) {
        count.toggleAttribute('hidden', !capped);
        count.textContent = capped ? `${input.value.length}/${input.maxLength}` : '';
      }
      clear?.toggleAttribute('hidden', input.value.length === 0);
    }
  }

  /**
   * Monogram or comments input. Monogram is sanitised to letters + upper-cased in place.
   * @param {Event} event
   */
  input(event) {
    const target = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (event.target);
    if (this.dataset.controlType === 'monogram') {
      const cleaned = target.value.toUpperCase().replace(/[^A-Z]/g, '');
      if (target.value !== cleaned) target.value = cleaned;
    }
    this.#store?.updateControl(this.#key, target.value);
    if (this.dataset.controlType === 'comments') this.#syncNotesUi();
    this.#refreshRemove();
  }

  /** "Edit" on a filled monogram field: focus + select the input so the shopper can retype it. */
  editField() {
    const input = this.querySelector('.custom-customizer-control__input');
    if (input instanceof HTMLInputElement) {
      input.focus({ preventScroll: true });
      input.select();
    }
  }

  /** Input focused: enter editing. CSS swaps Edit for the clear-x + counter, reveals Preview/Done,
   *  and on mobile turns the control into the keyboard-pinned sheet ({@link #setEditing}). */
  focusField() {
    clearTimeout(this.#editTimer);
    this.#setEditing(true);
    this.#refreshFieldCount();
    this.#syncSheetRows();
  }

  /**
   * Input blurred: leave editing. If focus stays inside this control (another message row, a
   * clear button, Preview / Done), editing continues; otherwise defer the exit briefly as a
   * fallback so a tap on a tool that doesn't take focus still registers.
   * @param {Event} event
   */
  blurField(event) {
    const next = /** @type {FocusEvent} */ (event).relatedTarget;
    if (next instanceof Element && this.contains(next)) return;
    clearTimeout(this.#editTimer);
    this.#editTimer = window.setTimeout(() => this.#setEditing(false), 150);
  }

  /**
   * Enter or leave the editing state. Beyond the `data-editing` attribute (the CSS hook), on mobile
   * this opens/closes the keyboard-pinned edit sheet: the control pins above the software keyboard
   * via @theme/custom.customizer-keyboard, the monogram placeholder swaps to its editing copy, and
   * the step body's scroll position is saved/restored (the browser may scroll the hidden body
   * trying to reveal the focused input).
   * @param {boolean} active
   */
  #setEditing(active) {
    const was = this.hasAttribute('data-editing');
    this.toggleAttribute('data-editing', active);
    if (active === was) return;

    const body = this.closest('.custom-customizer-step__body');
    if (active) {
      if (!CustomizerControl.#mobileSheet.matches) return;
      this.#sheetScrollTop = body instanceof HTMLElement ? body.scrollTop : 0;
      this.#swapPlaceholder(true);
      openKeyboardSheet(this);
    } else {
      closeKeyboardSheet(this);
      this.#swapPlaceholder(false);
      this.#clearSheetRows();
      if (body instanceof HTMLElement) body.scrollTop = this.#sheetScrollTop;
    }
  }

  /**
   * Swap the monogram input's placeholder to its editing copy while the sheet is open, and back.
   * A no-op for inputs without `data-editing-placeholder`.
   * @param {boolean} editing
   */
  #swapPlaceholder(editing) {
    const input = this.querySelector('.custom-customizer-control__input');
    if (!(input instanceof HTMLInputElement) || !input.dataset.editingPlaceholder) return;
    if (editing) {
      input.dataset.idlePlaceholder ??= input.placeholder;
      input.placeholder = input.dataset.editingPlaceholder;
    } else if (input.dataset.idlePlaceholder !== undefined) {
      input.placeholder = input.dataset.idlePlaceholder;
    }
  }

  /**
   * Keep the input focused while the shopper presses one of the editing-only tools (the clear-x,
   * or the mobile Preview / Done bar). On macOS Safari a `<button>` is not focusable by press, so
   * the input blurs with a null `relatedTarget` and the 150 ms exit timer can hide the tool before
   * its click lands. Preventing the press default keeps focus, so the click always fires.
   *
   * Mouse pointers ONLY: on touch, `preventDefault()` on the pointer swallows the tap's click
   * entirely, and touch focuses/blurs the field correctly on its own.
   * @param {PointerEvent} event
   */
  #keepFieldFocus = (event) => {
    if (event.pointerType !== 'mouse') return;
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest(
        '.custom-customizer-control__field-tools, .custom-customizer-control__field-ctas, .custom-customizer-control__line-clear'
      )
    ) {
      event.preventDefault();
    }
  };

  /** Clear-x: empty the field and stay in editing (refocus the input). */
  clearField() {
    const input = this.querySelector('.custom-customizer-control__input');
    if (input instanceof HTMLInputElement) {
      input.value = '';
      input.focus({ preventScroll: true });
    }
    this.#store?.updateControl(this.#key, '');
    this.#refreshRemove();
  }

  /** "Done": commit + leave editing by blurring whichever of this control's inputs holds focus. */
  doneField() {
    clearTimeout(this.#editTimer);
    this.#setEditing(false);
    const active = document.activeElement;
    if (active instanceof HTMLElement && this.contains(active)) active.blur();
  }

  /** Monogram input: the native keyboard's Return acts as Done (a single-line field).
   * @param {KeyboardEvent} event */
  fieldKeydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.doneField();
  }

  /** "Preview": leave editing and open the hero preview full-screen. A no-op on an empty field,
   *  which the design greys out. */
  previewField() {
    if (!this.hasAttribute('data-filled')) return;
    clearTimeout(this.#editTimer);
    this.#setEditing(false);
    this.#preview()?.openFullscreen();
  }

  /** @returns {CustomizerPreview | null} This customizer's hero preview element, if present. */
  #preview() {
    const el = this.closest('custom-customizer')?.querySelector('custom-customizer-preview');
    return /** @type {CustomizerPreview | null} */ (el ?? null);
  }

  /**
   * Choice / font option: mark the chosen row and push its value (the row carries `data-value`).
   * @param {Event} event
   */
  pick(event) {
    const row = /** @type {Element} */ (event.target).closest('[data-value]');
    if (!(row instanceof HTMLElement)) return;
    for (const candidate of this.querySelectorAll('[data-value]')) {
      candidate.setAttribute('aria-checked', String(candidate === row));
    }
    const value = row.dataset.value ?? null;
    const changed = this.#store?.getControlValue(this.#key) !== value;
    const recorded = this.#store?.updateControl(this.#key, value);
    const isMode = isFaceModeControl({ key: this.#key, type: this.dataset.controlType });
    if (recorded && changed && value && isMode) {
      track(this, 'choice', { choice: 'engraving_type', choice_value: value });
    }
  }

  /** Mirror the STORE value onto every option row's `aria-checked`, so a server default and a pick
   * never light two cards, and a lit row is always a choice the cart and price will carry. */
  #reflectChoice = () => {
    const raw = this.#store?.getControlValue(this.#key);
    const target = typeof raw === 'string' ? raw : '';
    for (const row of this.querySelectorAll('[data-value]')) {
      const value = row.getAttribute('data-value');
      row.setAttribute('aria-checked', String(target !== '' && value === target));
    }
  };

  /** Mirror the store value into the comments textarea, since the DOM can re-render while the
   * store keeps the value, then refresh the counter/filled state. */
  #reflectComments = () => {
    const textarea = this.querySelector('textarea');
    if (!(textarea instanceof HTMLTextAreaElement)) return;
    const value = this.#store?.getControlValue(this.#key);
    const next = typeof value === 'string' ? value : '';
    if (textarea.value !== next) textarea.value = next;
    this.#syncNotesUi();
  };

  /** Refresh the comments `n/max` counter and the boxed field's filled border state. */
  #syncNotesUi() {
    const textarea = this.querySelector('textarea');
    if (!(textarea instanceof HTMLTextAreaElement)) return;
    this.toggleAttribute('data-filled', textarea.value.length > 0);
    const counter = this.querySelector('[data-notes-count]');
    if (counter && textarea.maxLength > 0) {
      counter.textContent = `${textarea.value.length}/${textarea.maxLength}`;
    }
  }

  /**
   * Upload (file picker): validate and apply the selected file.
   * @param {Event} event
   */
  upload(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const file = input.files?.[0] ?? null;
    if (file) void this.#applyUploadFile(file);
    // Clear so re-picking the same file re-fires `change` (e.g. right after a Remove).
    input.value = '';
  }

  /** Hide the invalid-file banner. Bound via `on:click='/dismissUploadError'`. */
  dismissUploadError() {
    delete this.dataset.error;
  }

  /** @returns {HTMLElement | null} The dropzone label (drag target). */
  get #uploadZone() {
    const zone = this.querySelector('.custom-customizer-upload');
    return zone instanceof HTMLElement ? zone : null;
  }

  /**
   * Accepted extensions (lowercase, no dot) from `data-accept`, else the broad default: web-safe
   * formats pass through, the rest (HEIC/HEIF/PDF/AVIF/BMP) are converted to JPEG by
   * custom.customizer-photo-ingest.js. Kept in sync with the Liquid fallback.
   * @returns {string[]}
   */
  get #accept() {
    const raw = this.dataset.accept ?? '';
    const list = raw
      .split(',')
      .map((entry) => entry.trim().toLowerCase().replace(/^\./, ''))
      .filter(Boolean);
    return list.length > 0
      ? list
      : ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'gif', 'avif', 'bmp', 'pdf'];
  }

  /**
   * Client validation (type + size) driving the dropzone's error states.
   * @param {File} file
   * @returns {'format' | 'size' | null} The failure kind, or null when the file is acceptable.
   */
  #validateUpload(file) {
    const accept = this.#accept;
    const extension = (file.name.split('.').pop() ?? '').toLowerCase();
    const mimes = new Set(
      accept.map((entry) => CustomizerControl.#MIME_BY_EXTENSION[entry]).filter(Boolean)
    );
    const typeOk = accept.includes(extension) || (file.type !== '' && mimes.has(file.type));
    if (!typeOk) return 'format';

    // 25,000,000 bytes: the live designer's client-side cap, unanimous across its uploaders.
    const maxBytes = Number(this.dataset.maxBytes) || 25_000_000;
    if (file.size > maxBytes) return 'size';
    return null;
  }

  /** Extension to MIME pairs, since a dropped file carries MIME and no extension. Covers more
   * than the default set so a product whose schema opts into another format still validates. */
  static #MIME_BY_EXTENSION = /** @type {Record<string, string>} */ ({
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
    pdf: 'application/pdf',
    heic: 'image/heic',
    heif: 'image/heif',
    avif: 'image/avif',
    bmp: 'image/bmp',
    tif: 'image/tiff',
    tiff: 'image/tiff',
  });

  /**
   * Surface the invalid-upload banner (leaves any prior photo untouched).
   * @param {'format' | 'size' | 'too-large' | 'decode'} kind
   */
  #showUploadError(kind) {
    const message =
      kind === 'size'
        ? this.dataset.errorSizeMessage
        : kind === 'too-large'
          ? (this.dataset.errorTooLargeMessage ?? this.dataset.errorSizeMessage)
          : kind === 'decode'
            ? (this.dataset.errorDecodeMessage ?? this.dataset.errorFormatMessage)
            : this.dataset.errorFormatMessage;
    const body = this.querySelector('[data-upload-error-body]');
    if (body instanceof HTMLElement) body.textContent = message ?? '';
    this.dataset.error = kind;
    this.#uploadErrorKind = kind;
  }

  /**
   * Validate, normalize, then either surface the error banner (leaving any prior photo untouched)
   * or push the file: object URL as the render value (the adapter draws from it) plus the `File`
   * retained via `setControlUpload`, the persistence source the cart-add path uploads from.
   * The store broadcast drives {@link #reflectUpload}, so the thumbnail/success state follows.
   *
   * Normalization (custom.customizer-photo-ingest.js) converts HEIC/HEIF/PDF/… to JPEG
   * before anything downstream sees the file. Web-safe formats resolve within the same task, so
   * the interim `processing` dropzone state never paints for them; a real conversion shows it
   * until the newest pick lands (`#ingestSeq` - a superseding pick or a Remove aborts this one).
   * @param {File} file
   */
  async #applyUploadFile(file) {
    const seq = ++this.#ingestSeq;
    this.#ingestStore = this.#store;
    const failure = this.#validateUpload(file);
    if (failure) {
      this.#ingestBusy = false;
      this.#store?.setUploadState(this.#key, 'error');
      this.#showUploadError(failure);
      track(this, 'photo_error', { reason: failure });
      return;
    }

    this.#ingestBusy = true;
    this.#store?.setUploadState(this.#key, 'processing');
    delete this.dataset.error; // a stale banner must not sit over the processing state
    const zone = this.#uploadZone;
    if (zone) {
      zone.dataset.uploadState = 'processing';
      // The visual processing span is only un-hidden by CSS, which screen readers don't reliably
      // announce, so its text is mirrored into the live region. No aria-busy: it mutes that region.
      const status = zone.querySelector('.custom-customizer-upload__status');
      const announcement = zone.querySelector('.custom-customizer-upload__processing');
      if (status) status.textContent = announcement?.textContent?.trim() ?? '';
    }

    /** @type {File | null} */
    let normalized = null;
    try {
      normalized = await normalizePhotoFile(file);
    } catch (error) {
      // Reported to the shopper as 'decode' below; logged so a decoder-fetch failure, a pdf.js
      // worker error and a genuinely corrupt file stay distinguishable in the field.
      console.warn('[customizer] photo ingest failed', error);
    }
    // A superseded pick must not overwrite whatever owns the UI now, but the newest ingest still
    // releases the busy flag even when disconnected, or a reattached node no-ops #reflectUpload.
    if (seq === this.#ingestSeq) this.#ingestBusy = false;
    if (seq !== this.#ingestSeq || !this.isConnected || this.#ingestStore !== this.#store) return;

    if (!normalized) {
      this.#store?.setUploadState(this.#key, 'error');
      this.#reflectUpload();
      this.#showUploadError('decode');
      track(this, 'photo_error', { reason: 'decode' });
      return;
    }
    // The upload limit sits above the cart's, so a photo too large to add even on its own is
    // refused now rather than at Add to cart.
    if (!fitsCartAdd({}, [{ name: this.#key, file: normalized }])) {
      this.#store?.setUploadState(this.#key, 'error');
      this.#reflectUpload();
      track(this, 'photo_error', { reason: 'too-large' });
      this.#showUploadError('too-large');
      return;
    }
    // A validation banner raised by a mid-conversion pick must not sit over the success state.
    delete this.dataset.error;
    // The normalized File serves the order and preview; its URL belongs to the store.
    // The old photo's crop, note and paid proof do not describe its replacement.
    this.#store?.setControlTransform(this.#key, null);
    this.#store?.setDesignRequest(this.#key, null);
    this.#store?.setControlUpload(this.#key, normalized);
    track(this, 'photo_upload');
    // The store broadcast already ran #reflectUpload synchronously; this direct call is the
    // fallback that still clears the processing state when the host/store isn't reachable.
    this.#reflectUpload();
    this.#refreshRemove();
    // Written last: each store update above reflects the zone, which clears this line.
    const status = this.#uploadZone?.querySelector('.custom-customizer-upload__status');
    if (status instanceof HTMLElement) status.textContent = status.dataset.done ?? '';
  }

  /** Reflect the control's store value into the dropzone: thumbnail + success/empty state. */
  #reflectUpload = () => {
    if (this.#ingestBusy) {
      if (this.#ingestStore === this.#store && !this.#stepSkipped()) return;
      this.#ingestSeq++;
      this.#ingestBusy = false;
      this.#ingestStore?.setUploadState(this.#key, null);
    }
    const zone = this.#uploadZone;
    if (!zone) return;
    const value = this.#store?.getControlValue(this.#key);
    const has = typeof value === 'string' && value !== '';
    if (this.#store?.uploadState(this.#key) === 'error' && !this.dataset.error)
      this.#showUploadError(this.#uploadErrorKind);
    zone.dataset.uploadState = has ? 'success' : 'empty';
    // Cleared only when the photo is gone: a reflect right after "Photo added" would cut it off.
    const status = zone.querySelector('.custom-customizer-upload__status');
    if (status && !has) status.textContent = '';
    const thumb = zone.querySelector('.custom-customizer-upload__thumb');
    if (thumb instanceof HTMLImageElement) {
      if (has) {
        thumb.src = value;
        thumb.hidden = false;
      } else {
        thumb.removeAttribute('src');
        thumb.hidden = true;
      }
    }
    this.#refreshRemove();
  };

  /** @returns {boolean} Whether this control's own step was explicitly declined. */
  #stepSkipped() {
    const store = this.#store;
    const step = store?.configSchema?.steps?.find((candidate) =>
      candidate.controls?.some((control) => control.key === this.#key)
    );
    return !!step && store?.steps[step.id]?.status === 'skipped';
  }

  /** @param {DragEvent} event */
  #onDragOver = (event) => {
    event.preventDefault();
    this.#uploadZone?.classList.add('is-dragover');
  };

  /** @param {DragEvent} event */
  #onDragLeave = (event) => {
    event.preventDefault();
    this.#uploadZone?.classList.remove('is-dragover');
  };

  /** @param {DragEvent} event */
  #onDrop = (event) => {
    event.preventDefault();
    this.#uploadZone?.classList.remove('is-dragover');
    const file = event.dataTransfer?.files?.[0] ?? null;
    if (file) void this.#applyUploadFile(file);
  };

  /** Open the shared confirm dialog for this control. Bound via `on:click='/requestRemove'`. */
  requestRemove() {
    this.dispatchEvent(
      new CustomizerConfirmRemoveEvent(this.#configId, this.#key, this.dataset.removeBody ?? null)
    );
  }

  /**
   * The shopper confirmed removal in the dialog: clear this control's inputs and the stored value.
   * @param {Event} event
   */
  #handleRemoveConfirmed = (event) => {
    const { configId, controlKey } = /** @type {CustomEvent} */ (event).detail;
    if (controlKey !== this.#key || configId !== this.#configId) return;

    for (const input of this.#lineInputs()) input.value = '';
    for (const input of this.querySelectorAll(
      '.custom-customizer-control__input, .custom-customizer-control__file'
    )) {
      if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)
        input.value = '';
    }
    if (this.dataset.controlType === 'upload') {
      // Abort any in-flight ingest conversion - its result must not land on a removed slot.
      this.#ingestSeq++;
      this.#ingestBusy = false;
      // Forget the retained File, the render URL, the crop and the design request with it: a note
      // or paid proof about a removed photo must not survive onto whatever is uploaded next.
      this.#store?.setControlFile(this.#key, null);
      this.#store?.setControlTransform(this.#key, null);
      this.#store?.setDesignRequest(this.#key, null);
      delete this.dataset.error;
    }
    this.#store?.updateControl(this.#key, this.dataset.controlType === 'text' ? '' : null);
    this.#refreshMessageUi();
    this.#refreshRemove();
  };

  /** Reflect the filled state: show the REMOVE pill and mark `data-filled` (CSS reveals Edit, or
   *  the clear-x + counter while editing). Called on every value change. */
  #refreshRemove() {
    const value = this.#store?.getControlValue(this.#key);
    const hasValue = typeof value === 'string' ? value.trim() !== '' : Boolean(value);
    this.toggleAttribute('data-filled', hasValue);
    this.querySelector('.custom-customizer-control__remove')?.toggleAttribute('hidden', !hasValue);
    this.#refreshFieldCount();
  }

  /** Update the monogram field's "n/max" counter. */
  #refreshFieldCount() {
    const input = this.querySelector('.custom-customizer-control__input');
    const count = this.querySelector('[data-field-count]');
    if (!(input instanceof HTMLInputElement) || !count) return;
    count.textContent = input.maxLength > 0 ? `${input.value.length}/${input.maxLength}` : '';
  }

  /** Render each font-style thumbnail's sample in its web font (loaded once via FontFace). */
  #applyFonts() {
    const buttons = this.querySelectorAll('.custom-customizer-control__font[data-font]');
    if (buttons.length === 0) return;

    const fonts = this.#readFontAssets();
    for (const button of buttons) {
      if (!(button instanceof HTMLElement)) continue;
      const asset = fonts[button.dataset.font ?? ''];
      const sample = button.querySelector('[data-font-sample]');
      if (!asset || !(sample instanceof HTMLElement)) continue;
      CustomizerControl.#loadFont(asset);
      sample.style.fontFamily = asset.family;
    }
  }

  /** @returns {Record<string, FontAsset>} */
  #readFontAssets() {
    const assets = /** @type {{ fonts?: Record<string, FontAsset> } | undefined} */ (
      readIsland(this.closest('custom-customizer'), 'assets')
    );
    return assets?.fonts ?? {};
  }

  /** Fonts already handed to the document, deduped by family across every control.
   * @type {Set<string>} */
  static #loaded = new Set();

  /** @param {FontAsset} asset */
  static #loadFont(asset) {
    if (!asset.url || CustomizerControl.#loaded.has(asset.family)) return;
    CustomizerControl.#loaded.add(asset.family);
    const face = new FontFace(asset.family, `url(${asset.url})`, { display: 'swap' });
    face
      .load()
      .then((loaded) => document.fonts.add(loaded))
      .catch(() => CustomizerControl.#loaded.delete(asset.family));
  }

  /** Show this control only when every `visibleWhen` condition holds. */
  #syncVisibility = () => {
    const store = this.#store;
    const declared = store?.configSchema?.steps?.some((step) =>
      step.controls?.some((control) => control.key === this.#key)
    );
    if (store && declared) {
      this.hidden = !store.isControlActive(this.#key);
      return;
    }
    const raw = this.dataset.visibleWhen;
    if (!raw) return;

    /** @type {VisibleCondition[]} */
    let conditions;
    try {
      conditions = JSON.parse(raw);
    } catch {
      return;
    }
    if (!Array.isArray(conditions) || conditions.length === 0) return;

    const visible = conditions.every((condition) => {
      const value = store?.getControlValue(condition.key);
      return typeof value === 'string' && condition.in.includes(value);
    });
    this.hidden = !visible;
  };
}

if (!customElements.get('custom-customizer-control')) {
  customElements.define('custom-customizer-control', CustomizerControl);
}
